import { useEffect, useState } from 'react';
import {
  Building2, ShieldCheck, Clock, Ban, Send, X, CheckCircle2, MapPin,
} from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState, StatusLine } from '../components/ui';

/**
 * Lets a freelance coach see every club they're eligible to teach at, why
 * they're eligible (open / approved / auto), and request affiliation with
 * clubs that don't auto-accept freelance coaches.
 */

type Club = {
  id: string;
  name: string;
  address: string | null;
  photos: string[] | null;
  accepts_freelance_coaches: boolean;
};

type Affiliation = {
  id: string;
  club_id: string;
  status: 'approved' | 'pending' | 'blocked';
  notes: string | null;
  created_at: string;
};

export default function CoachMyClubs({ coachId }: { coachId: string }) {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const [clubsRes, affRes] = await Promise.all([
      supabase.from('clubs')
        .select('id, name, address, photos, accepts_freelance_coaches')
        .eq('is_active', true)
        .order('name'),
      supabase.from('coach_affiliations')
        .select('id, club_id, status, notes, created_at')
        .eq('coach_id', coachId),
    ]);
    setClubs(((clubsRes.data ?? []) as any) as Club[]);
    setAffiliations((affRes.data ?? []) as Affiliation[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [coachId]);

  const requestAffiliation = async (clubId: string) => {
    setBusyId(clubId);
    setMsg(null);
    const { error } = await supabase.from('coach_affiliations').insert({
      coach_id: coachId, club_id: clubId, status: 'pending',
    });
    setBusyId(null);
    if (error) setMsg({ kind: 'error', text: error.message });
    else {
      setMsg({ kind: 'success', text: 'Request sent. The club will review it.' });
      load();
    }
  };

  const cancelRequest = async (affiliationId: string) => {
    setBusyId(affiliationId);
    setMsg(null);
    const { error } = await supabase.from('coach_affiliations').delete().eq('id', affiliationId);
    setBusyId(null);
    if (error) setMsg({ kind: 'error', text: error.message });
    else load();
  };

  // Categorise for display.
  const affByClub = new Map(affiliations.map((a) => [a.club_id, a] as const));

  type DisplayRow = {
    club: Club;
    status: 'open' | 'approved' | 'pending' | 'blocked' | 'closed';
    affiliation?: Affiliation;
  };
  const rows: DisplayRow[] = clubs.map((c) => {
    const aff = affByClub.get(c.id);
    if (aff?.status === 'approved') return { club: c, status: 'approved', affiliation: aff };
    if (aff?.status === 'pending')  return { club: c, status: 'pending',  affiliation: aff };
    if (aff?.status === 'blocked')  return { club: c, status: 'blocked',  affiliation: aff };
    if (c.accepts_freelance_coaches) return { club: c, status: 'open' };
    return { club: c, status: 'closed' };
  });

  const teachable = rows.filter((r) => ['open', 'approved'].includes(r.status));
  const pending   = rows.filter((r) => r.status === 'pending');
  const closed    = rows.filter((r) => r.status === 'closed');
  const blocked   = rows.filter((r) => r.status === 'blocked');

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Coach"
        title="Clubs I teach at"
        subtitle="Clubs open to freelancers let you teach right away. For others, send a request and wait for their approval."
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section title="Teaching here now" subtitle="Customers can book you at these clubs." className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-gray-500 text-center">Loading…</div>
        ) : teachable.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No clubs yet"
            description="Clubs open to freelancers will show here automatically. For others, send a request below."
          />
        ) : (
          <ClubList rows={teachable} busyId={busyId} onAct={() => {}} variant="teaching" />
        )}
      </Section>

      {pending.length > 0 && (
        <Section title="Pending requests" subtitle="Waiting for the club to approve you." className="p-0 overflow-hidden">
          <ClubList rows={pending} busyId={busyId}
            onAct={(row) => row.affiliation && cancelRequest(row.affiliation.id)}
            variant="pending" />
        </Section>
      )}

      <Section title="Clubs you could request" subtitle="Send a message to the club's admin and they'll review." className="p-0 overflow-hidden">
        {closed.length === 0 ? (
          <EmptyState icon={Building2} title="You've covered them all"
            description="Every club is either open, approved, or pending." />
        ) : (
          <ClubList rows={closed} busyId={busyId}
            onAct={(row) => requestAffiliation(row.club.id)}
            variant="request" />
        )}
      </Section>

      {blocked.length > 0 && (
        <Section title="Blocked" subtitle="These clubs have declined. You can't re-request without contacting them directly." className="p-0 overflow-hidden">
          <ClubList rows={blocked} busyId={busyId} onAct={() => {}} variant="blocked" />
        </Section>
      )}
    </div>
  );
}

function ClubList({
  rows, busyId, onAct, variant,
}: {
  rows: Array<{ club: Club; status: string; affiliation?: Affiliation }>;
  busyId: string | null;
  onAct: (row: any) => void;
  variant: 'teaching' | 'pending' | 'request' | 'blocked';
}) {
  return (
    <div className="divide-y divide-gray-100">
      {rows.map(({ club, status, affiliation }) => {
        const cover = club.photos?.[0];
        const targetId = variant === 'request' ? club.id : affiliation?.id;
        const disabled = busyId === targetId || variant === 'teaching' || variant === 'blocked';
        return (
          <div key={club.id} className="flex items-center gap-3 p-4 hover:bg-gray-50/60">
            <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {cover
                ? <img src={cover} alt="" className="w-full h-full object-cover" />
                : <Building2 className="w-5 h-5 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 truncate">{club.name}</span>
                {status === 'open'     && <Badge tone="lime" icon={ShieldCheck}>Open to freelancers</Badge>}
                {status === 'approved' && <Badge tone="green" icon={CheckCircle2}>Approved</Badge>}
                {status === 'pending'  && <Badge tone="amber" icon={Clock}>Pending</Badge>}
                {status === 'blocked'  && <Badge tone="red" icon={Ban}>Blocked</Badge>}
                {status === 'closed'   && <Badge tone="gray">Closed by default</Badge>}
              </div>
              {club.address && (
                <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {club.address}
                </div>
              )}
            </div>
            {variant === 'request' && (
              <Button size="sm" icon={Send}
                loading={busyId === club.id}
                disabled={disabled}
                onClick={() => onAct({ club, status, affiliation })}>
                Request
              </Button>
            )}
            {variant === 'pending' && (
              <Button size="sm" variant="danger" icon={X}
                loading={busyId === affiliation?.id}
                disabled={disabled}
                onClick={() => onAct({ club, status, affiliation })}>
                Cancel
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
